import { toNumericId } from "@/features/tuition/utils/to-numeric-id";
import {
  GetCalculationCycleResponseFromJSON,
  IdNameResultOfintFromJSON,
} from "@/openapi/generated";
import type { ApiResponse } from "@/openapi/generated/runtime";
import type {
  CalculationCycle,
  CalculationCycleDetail,
  CalculationCyclePage,
} from "../types";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function parseNamedEntity(
  value: unknown,
): CalculationCycleDetail["member"] | null {
  if (value == null) return null;
  return IdNameResultOfintFromJSON(value);
}

export function parseCalculationCycleDetail(
  value: unknown,
): CalculationCycleDetail | null {
  const raw = asRecord(value);
  if (!raw || raw.id == null) return null;

  return {
    id: raw.id as CalculationCycleDetail["id"],
    calculationCycleId:
      raw.calculationCycleId as CalculationCycleDetail["calculationCycleId"],
    transactionCode: String(raw.transactionCode ?? ""),
    member: parseNamedEntity(raw.member) ?? { id: 0, name: "" },
    student: parseNamedEntity(raw.student) ?? { id: 0, name: "" },
    class: parseNamedEntity(raw.class ?? raw._class),
    location: parseNamedEntity(raw.location),
    season: parseNamedEntity(raw.season),
    amount: Number(raw.amount ?? 0),
    lineType: String(raw.lineType ?? ""),
    description: raw.description == null ? null : String(raw.description),
    note: raw.note == null ? null : String(raw.note),
    isSynced: Boolean(raw.isSynced),
  };
}

export function parseCalculationCycle(value: unknown): CalculationCycle | null {
  const raw = asRecord(value);
  if (!raw || raw.id == null) return null;

  const base = GetCalculationCycleResponseFromJSON(raw);
  if (!toNumericId(base.id)) return null;

  return {
    ...base,
    isClosed: typeof raw.isClosed === "boolean" ? raw.isClosed : null,
    details: Array.isArray(raw.details)
      ? raw.details
          .map(parseCalculationCycleDetail)
          .filter((detail): detail is CalculationCycleDetail => detail != null)
      : [],
  };
}

export function parseCurrentCalculationCycle(
  value: unknown,
): CalculationCycle | null {
  const raw = asRecord(value);
  if (!raw) return null;

  if (Array.isArray(raw.items)) {
    return parseCalculationCycle(raw.items[0]);
  }

  return parseCalculationCycle(raw);
}

export function parseCalculationCyclePage(
  value: unknown,
): CalculationCyclePage {
  const raw = asRecord(value);
  const items = Array.isArray(raw?.items)
    ? raw.items
        .map(parseCalculationCycle)
        .filter((cycle): cycle is CalculationCycle => cycle != null)
    : [];

  return {
    items,
    lastSelectedId: raw?.lastSelectedId,
    pageNumber: raw?.pageNumber,
    pageSize: raw?.pageSize,
    totalCount: raw?.totalCount,
    hasNextPage: Boolean(raw?.hasNextPage),
    hasPreviousPage: Boolean(raw?.hasPreviousPage),
  };
}

export async function readApiJson(
  response: ApiResponse<unknown>,
): Promise<unknown> {
  return response.raw.json();
}

/** 204 / empty success bodies must not be parsed as JSON (that throws and retriggers the mutation). */
export async function readEmptyOkJson(
  response: ApiResponse<unknown>,
): Promise<unknown> {
  const { raw } = response;
  if (raw.status === 204) {
    return null;
  }
  const text = await raw.text();
  if (!text.trim()) {
    return null;
  }
  return JSON.parse(text) as unknown;
}
